/**
 * Shared constants and math for the spotlight stencil's proportional feather (Issue #1496).
 *
 * The feather (soft edge of the spotlight cutout) is expressed as a percentage of the
 * highlighted box's on-screen size, so the halo stays visually constant relative to the
 * object at every zoom level. These live in their own module so the settings UI and
 * `TabService` can reference them without importing the overlay component.
 */

/** Default feather size, as a percentage of the on-screen (box width + box height). */
export const DEFAULT_FEATHER_PERCENT = 5;

/** Minimum feather percentage exposed by the Power User slider (0% = hard edge). */
export const MIN_FEATHER_PERCENT = 0;

/** Maximum feather percentage exposed by the Power User slider. */
export const MAX_FEATHER_PERCENT = 15;

/** Step size of the Power User feather slider, in percent. */
export const FEATHER_PERCENT_STEP = 0.5;

/**
 * Lower clamp for the resolved feather, in screen pixels.
 * Keeps the spotlight edge from collapsing into a hard line on tiny boxes.
 */
export const MIN_FEATHER_PX = 6;

/**
 * Upper clamp for the resolved feather, in screen pixels.
 * Keeps a near-full-page box from blurring the entire overlay.
 */
export const MAX_FEATHER_PX = 150;

/**
 * Coerces a geometry value to a finite number, defaulting non-finite input to 0.
 *
 * A NaN or Infinity reaching the SVG geometry attributes produces browser rendering
 * warnings and a stencil that silently fails to draw, so bad values (a NaN canvas
 * scale, an unparsed bounding box) are contained here rather than propagated.
 * Preferred over the `|| 0` idiom, which also swallows legitimate falsy values.
 */
export function toFiniteDimension(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/**
 * Clamps a feather percentage into the supported slider range, repairing
 * non-finite input (NaN / Infinity) back to the default.
 */
export function clampFeatherPercent(percent: number): number {
  const value = Number(percent);
  if (!Number.isFinite(value)) {
    return DEFAULT_FEATHER_PERCENT;
  }
  return Math.min(MAX_FEATHER_PERCENT, Math.max(MIN_FEATHER_PERCENT, value));
}

/**
 * Resolves a feather percentage into screen pixels for an on-screen box.
 *
 * The inputs are expected to already include the canvas zoom scale, which is what makes
 * the feather constant *relative to the object* at every zoom level: doubling the zoom
 * doubles both the box and its halo.
 *
 * - `0%` is honoured exactly and yields a hard edge (no blur).
 * - Any positive percentage is clamped to `[MIN_FEATHER_PX, MAX_FEATHER_PX]` so tiny
 *   boxes keep a visible soft edge and huge boxes don't blur the whole overlay.
 *
 * @param featherPercent Percentage of (width + height), typically 0–15.
 * @param scaledWidth On-screen box width in CSS pixels.
 * @param scaledHeight On-screen box height in CSS pixels.
 */
export function resolveFeatherPx(featherPercent: number, scaledWidth: number, scaledHeight: number): number {
  const percent = clampFeatherPercent(featherPercent);

  // Explicit "no feather" request from settings - keep the edge hard.
  if (percent === 0) {
    return 0;
  }

  const combinedSize = Math.max(0, scaledWidth) + Math.max(0, scaledHeight);
  if (!Number.isFinite(combinedSize) || combinedSize <= 0) {
    return MIN_FEATHER_PX;
  }

  const raw = (percent / 100) * combinedSize;
  return Math.min(MAX_FEATHER_PX, Math.max(MIN_FEATHER_PX, raw));
}
